import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '../../../shared/components/ui/Button.js'
import { Modal } from '../../../shared/components/ui/Modal.js'
import { TenantStatusBadge, PlanBadge } from '../../../shared/components/ui/Badge.js'
import { api } from './api.js'
import { InfoRow, EmpresaStatusBadge } from './ErrorBox.js'
import { AdicionarEmpresaModal } from './AdicionarEmpresaModal.js'
import { EditarEmpresaModal } from './EditarEmpresaModal.js'
import {
  formatDate,
  formatCNPJ,
  type Conta,
  type ContaDetalhe,
  type ContaStatus,
  type EmpresaDaConta,
  type EmpresaStatus,
} from './types.js'

interface Props {
  conta: Conta
  onClose: () => void
  onEdit: (conta: Conta) => void
  onRefresh: () => void
}

/**
 * Painel de detalhes com badges de status, banner quando suspensa, info da conta,
 * cards de empresas com API key copy + toggle suspender/reativar individual,
 * e ações de footer (editar conta + toggle suspender/reativar conta-mãe).
 *
 * Porte fiel do ContaDetailPanel do super.old (web-v2). Os endpoints /api/v1/super/*
 * são reescritos pro proxy /api/servers/{id}/* pelo shim em ./api.js.
 */
export function ContaDetailPanel({ conta, onClose, onEdit, onRefresh }: Props) {
  const [detalhe, setDetalhe] = useState<ContaDetalhe | null>(null)
  const [loadingDetalhe, setLoading] = useState(true)
  const [adicionarOpen, setAdicionarOpen] = useState(false)
  const [editarEmpresa, setEditarEmpresa] = useState<EmpresaDaConta | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Status local — permite atualizar UI sem fechar painel
  const [localContaStatus, setLocalContaStatus] = useState<ContaStatus>(conta.status)
  const [suspendingConta, setSuspendingConta] = useState(false)

  const [empresaStatus, setEmpresaStatus] = useState<Record<string, EmpresaStatus>>({})
  const [suspendingEmpresa, setSuspendingEmpresa] = useState<string | null>(null)

  const fetchDetalhe = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const data = await api.get<ContaDetalhe>(`/api/v1/super/contas/${conta.id}`)
        setDetalhe(data)
        const statusMap: Record<string, EmpresaStatus> = {}
        data.empresas?.forEach((e) => {
          statusMap[e.id] = e.status ?? 'active'
        })
        setEmpresaStatus(statusMap)
      } catch {
        setDetalhe(null)
      } finally {
        setLoading(false)
      }
    },
    [conta.id],
  )

  useEffect(() => {
    fetchDetalhe()
  }, [fetchDetalhe])

  function handleCopyApiKey(apiKey: string, id: string) {
    navigator.clipboard.writeText(apiKey)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  async function handleToggleContaStatus() {
    const newStatus: ContaStatus =
      localContaStatus === 'suspended' ? 'active' : 'suspended'
    setSuspendingConta(true)
    try {
      await api.patch(`/api/v1/super/contas/${conta.id}`, { status: newStatus })
      setLocalContaStatus(newStatus)
      onRefresh()
      fetchDetalhe(true)
      toast.success(
        newStatus === 'suspended' ? 'Conta suspensa' : 'Conta reativada',
        { description: conta.nome },
      )
    } catch (err) {
      toast.error('Erro ao alterar status', {
        description: err instanceof Error ? err.message : 'Tente novamente',
      })
    } finally {
      setSuspendingConta(false)
    }
  }

  async function handleToggleEmpresaStatus(empresa: EmpresaDaConta) {
    const current = empresaStatus[empresa.id] ?? empresa.status
    const newStatus: EmpresaStatus = current === 'suspended' ? 'active' : 'suspended'
    setSuspendingEmpresa(empresa.id)
    try {
      await api.patch(`/api/v1/super/empresas/${empresa.id}`, { status: newStatus })
      setEmpresaStatus((prev) => ({ ...prev, [empresa.id]: newStatus }))
      toast.success(
        newStatus === 'suspended' ? 'Empresa suspensa' : 'Empresa reativada',
        { description: empresa.nome },
      )
    } catch (err) {
      toast.error('Erro ao alterar status da empresa', {
        description: err instanceof Error ? err.message : 'Tente novamente',
      })
    } finally {
      setSuspendingEmpresa(null)
    }
  }

  const displayContaStatus = localContaStatus
  const contaSuspended = ['suspended', 'cancelled'].includes(displayContaStatus)

  return (
    <>
      <Modal open onClose={onClose} title={`Detalhes — ${conta.nome}`} size="xl">
        <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
          {/* Badges */}
          <div className="flex gap-2 mb-4">
            <TenantStatusBadge status={displayContaStatus} />
            <PlanBadge plan={conta.plan} />
          </div>

          {/* Banner suspensa */}
          {contaSuspended && (
            <div className="flex items-center gap-2.5 p-3 rounded-lg bg-orange-50 border border-orange-200 mb-4">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" className="shrink-0">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <p className="text-xs text-orange-700 font-medium">
                Conta suspensa — edições bloqueadas. Reative a conta para realizar alterações.
              </p>
            </div>
          )}

          {/* Informações */}
          <div className="pb-4 mb-4 border-b border-[#f1f5f9]">
            <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide mb-3">
              Informações da conta
            </h3>
            <dl className="flex flex-col gap-3">
              <InfoRow label="E-mail" value={conta.email} />
              <InfoRow label="Telefone" value={conta.telefone ?? ''} />
              <InfoRow label="Criado em" value={formatDate(conta.created_at)} />
            </dl>
          </div>

          {/* Empresas */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">
                Empresas ({detalhe?.empresas?.length ?? 0})
              </h3>
              {!contaSuspended && (
                <button
                  onClick={() => setAdicionarOpen(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-[#0a4ee3] hover:text-[#083eb5] transition-colors"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Adicionar empresa
                </button>
              )}
            </div>

            {loadingDetalhe ? (
              <div className="flex flex-col gap-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-28 bg-[#f8fafc] rounded-xl animate-pulse" />
                ))}
              </div>
            ) : !detalhe?.empresas?.length ? (
              <div className="text-center py-6">
                <div className="w-10 h-10 rounded-xl bg-[#f8fafc] flex items-center justify-center mx-auto mb-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
                <p className="text-sm text-[#94a3b8]">Nenhuma empresa cadastrada.</p>
                {!contaSuspended && (
                  <button
                    onClick={() => setAdicionarOpen(true)}
                    className="text-xs text-[#0a4ee3] hover:underline mt-1"
                  >
                    Adicionar agora
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {detalhe.empresas.map((empresa) => {
                  const currentStatus = empresaStatus[empresa.id] ?? empresa.status ?? 'active'
                  const isSuspending = suspendingEmpresa === empresa.id
                  const canReactivate = !(currentStatus === 'suspended' && contaSuspended)
                  const editBlocked = contaSuspended
                  return (
                    <div key={empresa.id} className="p-3.5 rounded-xl bg-[#f8fafc] border border-[#f1f5f9]">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-[#0f172a]">{empresa.nome}</p>
                            <EmpresaStatusBadge status={currentStatus} />
                          </div>
                          {empresa.razao_social && (
                            <p className="text-xs text-[#64748b] truncate mt-0.5">{empresa.razao_social}</p>
                          )}
                          {empresa.cnpj && (
                            <p className="text-xs font-mono text-[#94a3b8] mt-0.5">{formatCNPJ(empresa.cnpj)}</p>
                          )}
                        </div>

                        <button
                          onClick={() => !editBlocked && setEditarEmpresa(empresa)}
                          disabled={editBlocked}
                          title={editBlocked ? 'Reative a conta para editar esta empresa.' : 'Editar empresa'}
                          className={`shrink-0 p-1.5 rounded-lg transition-colors ${
                            editBlocked
                              ? 'opacity-30 cursor-not-allowed text-[#94a3b8]'
                              : 'text-[#94a3b8] hover:text-[#0a4ee3] hover:bg-[#eff4ff]'
                          }`}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>

                      {/* API Key */}
                      <div className="flex items-center gap-2 bg-white border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 mb-2.5">
                        <code className="flex-1 text-[11px] font-mono text-[#64748b] truncate">
                          {empresa.api_key}
                        </code>
                        <button
                          onClick={() => handleCopyApiKey(empresa.api_key, empresa.id)}
                          className="flex items-center gap-1 shrink-0 text-[10px] font-medium text-[#94a3b8] hover:text-[#0a4ee3] transition-colors border border-[#e2e8f0] rounded px-1.5 py-0.5 bg-[#f8fafc] hover:bg-[#eff4ff] hover:border-[#0a4ee3]"
                          title="Copiar API Key"
                        >
                          {copiedId === empresa.id ? (
                            <>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              <span className="text-green-600">Copiado!</span>
                            </>
                          ) : (
                            <>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                              Copiar
                            </>
                          )}
                        </button>
                      </div>

                      <button
                        onClick={() => handleToggleEmpresaStatus(empresa)}
                        disabled={isSuspending || !canReactivate}
                        title={
                          !canReactivate
                            ? 'Reative a conta primeiro para poder reativar esta empresa.'
                            : undefined
                        }
                        className={`w-full text-xs font-medium py-1.5 rounded-lg border transition-colors ${
                          isSuspending || !canReactivate
                            ? 'opacity-40 cursor-not-allowed bg-[#f8fafc] border-[#e2e8f0] text-[#94a3b8]'
                            : currentStatus === 'suspended'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                        }`}
                      >
                        {isSuspending
                          ? 'Aguarde…'
                          : currentStatus === 'suspended'
                            ? '↑ Reativar empresa'
                            : '⊘ Suspender empresa'}
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2 pt-4 mt-4 border-t border-[#f1f5f9]">
          <Button
            onClick={() => {
              onEdit(conta)
              onClose()
            }}
            className="w-full"
            disabled={contaSuspended}
            title={contaSuspended ? 'Reative a conta para editar.' : undefined}
          >
            Editar dados da conta
          </Button>
          <button
            onClick={handleToggleContaStatus}
            disabled={suspendingConta}
            className={`w-full text-sm font-medium py-2 rounded-lg border transition-colors ${
              suspendingConta
                ? 'opacity-50 cursor-not-allowed'
                : displayContaStatus === 'suspended'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                  : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
            }`}
          >
            {suspendingConta
              ? 'Aguarde…'
              : displayContaStatus === 'suspended'
                ? '↑ Reativar conta'
                : '⊘ Suspender conta'}
          </button>
        </div>
      </Modal>

      <AdicionarEmpresaModal
        open={adicionarOpen}
        onClose={() => {
          setAdicionarOpen(false)
          fetchDetalhe(true)
          onRefresh()
        }}
        onSaved={() => {}}
        contaId={conta.id}
      />

      {editarEmpresa && (
        <EditarEmpresaModal
          open={!!editarEmpresa}
          onClose={() => setEditarEmpresa(null)}
          onSaved={() => {
            fetchDetalhe(true)
            onRefresh()
          }}
          empresa={editarEmpresa}
        />
      )}
    </>
  )
}
